/**
 * Leitura estruturada do ambiente.
 *
 * As quatro leituras que um projetista faz antes de cotar um elemento — o par
 * de tipologias, a pessoa, a parede e o cômodo — precisam existir como dado
 * produzido pelo sistema, entregue ao planejador antes da decisão e ao crítico
 * independentemente do plano. Este módulo define a forma desse dado
 * (`EnvironmentReading`), a inferência da função do cômodo a partir do que a
 * cena contém, e a validação de um plano contra a leitura: cota absoluta com
 * linha de referência, faixa da atividade, alcance e envelope de operação.
 *
 * O cálculo geométrico da leitura vive no editor (que tem a cena); aqui ficam
 * a forma e as regras, para que validador, ferramentas e testes compartilhem
 * exatamente a mesma lógica.
 */

import { z } from "zod";
import type { TaskPlanProposal, TaskSpatialDecision } from "./orchestration";
import { planSubjectsRefer } from "./orchestration";
import {
  classifyInstallationTypology,
  isSuspendedTypology,
  verticalClearanceRule,
  type InstallationTypology,
  type TypologyDescriptor,
} from "./typology";
import {
  ACTIVITY_LABELS,
  ROOM_FUNCTION_LABELS,
  STANDING_ADULT,
  inferOpeningKind,
  operationEnvelopeAssessment,
  reachAssessment,
  verticalPlacementBand,
  type ActivityConfidence,
  type RoomFunction,
  type SurfaceActivity,
} from "./use-zones";

export const roomFunctionSchema = z.enum([
  "kitchen", "dining", "living", "bedroom", "bathroom", "office", "laundry", "leisure", "service", "circulation", "undefined",
]);
export const surfaceActivitySchema = z.enum([
  "work_standing", "work_seated", "support", "display", "seating", "sleeping", "appliance", "circulation", "unknown",
]);
export const installationTypologySchema = z.enum([
  "upper_cabinet", "shelf", "wall_panel", "mirror_or_art", "hood", "lighting", "base_cabinet", "countertop", "low_furniture",
  "table", "tall_unit", "appliance", "seating", "bed", "fixture", "unknown",
]);

export const environmentLineSchema = z.object({
  heightMm: z.number(),
  kind: z.enum(["top", "bottom", "ceiling", "opening-head", "opening-sill"]),
  family: z.enum(["suspended", "floor-standing", "opening", "envelope"]),
  sourceElementId: z.string().optional(),
  /** Todos os elementos cuja cota foi fundida nesta linha (mesma altura, tipo e família). */
  sourceElementIds: z.array(z.string()).optional(),
  sourceName: z.string().optional(),
});

/**
 * O que o módulo TEM, contado da árvore de componentes registrada na
 * importação. Ausente em modelos sem árvore (SKP simples): aí só a foto
 * confirma portas e gavetas. É a evidência que distingue "móvel de 3 portas"
 * de "gaveteiro" quando os nomes não dizem.
 */
export const elementComponentrySchema = z.object({
  portas: z.number().int().nonnegative().optional(),
  gavetas: z.number().int().nonnegative().optional(),
  basculantes: z.number().int().nonnegative().optional(),
  prateleiras: z.number().int().nonnegative().optional(),
  nichos: z.number().int().nonnegative().optional(),
}).describe("Contagem de portas/gavetas/basculantes/prateleiras/nichos registrada na árvore de componentes do módulo. Ausente = sem árvore; confirme pela foto.");

export type ElementComponentry = z.infer<typeof elementComponentrySchema>;

export const environmentElementSchema = z.object({
  elementId: z.string(),
  name: z.string(),
  wallId: z.string().nullable(),
  typology: z.object({
    kind: installationTypologySchema,
    label: z.string(),
    confidence: z.enum(["high", "medium", "low"]),
    basis: z.enum(["language", "installation", "proportion", "none"]),
  }),
  mount: z.enum(["wall", "floor", "ceiling", "element", "unconstrained", "unknown"]),
  suspended: z.boolean(),
  bottomMm: z.number(),
  topMm: z.number(),
  widthMm: z.number(),
  depthMm: z.number(),
  openingKind: z.enum(["top_hinged", "side_hinged", "drawer", "sliding", "lift_up", "unknown"]).optional(),
  /** Projeção em planta (mm, eixos do mundo): quem está sobre quem se decide por sobreposição, não pela parede em comum. */
  footprintMm: z.object({ minXMm: z.number(), maxXMm: z.number(), minZMm: z.number(), maxZMm: z.number() }).optional(),
  componentry: elementComponentrySchema.optional(),
});

/** True when the two plan footprints overlap by at least `minimumMm` on both axes. */
export function footprintsOverlap(
  left: { minXMm: number; maxXMm: number; minZMm: number; maxZMm: number } | null | undefined,
  right: { minXMm: number; maxXMm: number; minZMm: number; maxZMm: number } | null | undefined,
  minimumMm = 1,
): boolean {
  if (!left || !right) return false;
  const overlapX = Math.min(left.maxXMm, right.maxXMm) - Math.max(left.minXMm, right.minXMm);
  const overlapZ = Math.min(left.maxZMm, right.maxZMm) - Math.max(left.minZMm, right.minZMm);
  return overlapX >= minimumMm && overlapZ >= minimumMm;
}

export const environmentSurfaceSchema = z.object({
  elementId: z.string(),
  name: z.string(),
  topMm: z.number(),
  depthMm: z.number(),
  activity: surfaceActivitySchema,
  confidence: z.enum(["declared", "high", "medium", "low"]),
  basis: z.string(),
});

export const environmentWallSchema = z.object({
  wallId: z.string(),
  lengthMm: z.number(),
  lines: z.array(environmentLineSchema),
  elementIds: z.array(z.string()),
});

export const environmentReadingSchema = z.object({
  roomId: z.string().nullable(),
  roomName: z.string().nullable(),
  roomFunction: z.object({
    value: roomFunctionSchema,
    confidence: z.enum(["declared", "inferred", "undefined"]),
    evidence: z.array(z.string()),
  }),
  userProfile: z.object({
    id: z.string(),
    label: z.string(),
    statureMm: z.tuple([z.number(), z.number()]),
    reach: z.object({
      primeMm: z.tuple([z.number(), z.number()]),
      acceptableMm: z.tuple([z.number(), z.number()]),
      headZoneMm: z.tuple([z.number(), z.number()]),
      storageTopMaxMm: z.number(),
      clearUnderFixedInCirculationMm: z.number(),
    }),
  }),
  ceilingHeightMm: z.number(),
  walls: z.array(environmentWallSchema),
  elements: z.array(environmentElementSchema),
  surfaces: z.array(environmentSurfaceSchema),
  notes: z.array(z.string()),
  summary: z.string(),
});

export type EnvironmentReading = z.infer<typeof environmentReadingSchema>;
export type EnvironmentLine = z.infer<typeof environmentLineSchema>;
export type EnvironmentElement = z.infer<typeof environmentElementSchema>;
export type EnvironmentSurface = z.infer<typeof environmentSurfaceSchema>;

/** Duas cotas lêem como a mesma linha até esta diferença. */
export const DATUM_TOLERANCE_MM = 25;

const ROOM_SIGNALS: Array<{ room: RoomFunction; terms: RegExp; kinds: InstallationTypology[]; weight: number }> = [
  { room: "kitchen", terms: /\bcozinha\b|cooktop|fogao|fogão|forno|geladeira|refrigerador|micro-?ondas|lava-?loucas|lava loucas|coifa|depurador|exaustor|paneleiro|\bpia\b|cuba de cozinha|bancada de cozinha|torre quente|torre geladeira/, kinds: ["hood", "countertop"], weight: 2 },
  { room: "bathroom", terms: /banheiro|lavabo|\bcuba\b|vaso sanitario|bacia sanitaria|\bbox\b|chuveiro|ducha|lavatorio|banheira|papeleira|toalheiro|espelheira/, kinds: [], weight: 2 },
  { room: "bedroom", terms: /dormitorio|quarto|\bcama\b|colchao|berco|beliche|cabeceira|criado|roupeiro|guarda-?roupa|closet|comoda|penteadeira/, kinds: ["bed"], weight: 2 },
  { room: "living", terms: /sala de estar|\bestar\b|\bsofa\b|poltrona|\brack\b|painel de tv|painel tv|\btv\b|home theater|estante|mesa de centro|mesa lateral|tapete/, kinds: ["seating"], weight: 2 },
  { room: "dining", terms: /sala de jantar|\bjantar\b|mesa de jantar|buffet|\bbufe\b|aparador|cristaleira|cadeira de jantar/, kinds: [], weight: 2 },
  { room: "office", terms: /escritorio|home office|escrivaninha|mesa de trabalho|mesa de estudo|cadeira de escritorio|gaveteiro volante|consultorio/, kinds: [], weight: 2 },
  { room: "laundry", terms: /lavanderia|area de servico|\btanque\b|lavadora|secadora|maquina de lavar|varal|tanquinho/, kinds: [], weight: 2 },
  { room: "leisure", terms: /churrasqueira|gourmet|piscina|espreguicadeira|\bbar\b|varanda|deck|jardim|area de lazer|sauna|ofuro/, kinds: [], weight: 2 },
  { room: "service", terms: /deposito|despensa|dml|casa de maquinas|garagem|oficina/, kinds: [], weight: 1 },
  { room: "circulation", terms: /\bhall\b|corredor|circulacao|escada|patamar/, kinds: [], weight: 1 },
];

function normalizeText(value: string | null | undefined): string {
  return (value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("pt-BR");
}

/**
 * Função do cômodo a partir da linguagem dos elementos e das tipologias. Sem
 * perfil declarado, é uma hipótese: duas famílias fortes e incompatíveis
 * (sofá e roupeiro, cama e bancada de cozinha) deixam a função indefinida,
 * com a evidência listada para o planejador declarar a premissa.
 */
export function inferRoomFunction(input: {
  roomName?: string | null;
  elements: ReadonlyArray<{ name: string; category?: string | null; kind: InstallationTypology }>;
  declared?: RoomFunction | null;
}): EnvironmentReading["roomFunction"] {
  if (input.declared && input.declared !== "undefined") {
    return { value: input.declared, confidence: "declared", evidence: ["perfil do cômodo declarado no projeto"] };
  }
  const scores = new Map<RoomFunction, { score: number; evidence: string[] }>();
  const bump = (room: RoomFunction, amount: number, evidence: string) => {
    const entry = scores.get(room) ?? { score: 0, evidence: [] };
    entry.score += amount;
    if (!entry.evidence.includes(evidence)) entry.evidence.push(evidence);
    scores.set(room, entry);
  };
  const roomName = normalizeText(input.roomName);
  for (const signal of ROOM_SIGNALS) {
    if (roomName && signal.terms.test(roomName)) bump(signal.room, signal.weight * 2, `nome do ambiente: ${input.roomName}`);
  }
  for (const element of input.elements) {
    const text = `${normalizeText(element.name)} ${normalizeText(element.category)}`;
    for (const signal of ROOM_SIGNALS) {
      if (signal.terms.test(text)) bump(signal.room, signal.weight, element.name);
      else if (signal.kinds.includes(element.kind)) bump(signal.room, 1, `${element.name} (${element.kind})`);
    }
  }
  const ranked = [...scores.entries()].sort((left, right) => right[1].score - left[1].score);
  if (!ranked.length) return { value: "undefined", confidence: "undefined", evidence: ["nenhum elemento característico de uma função"] };
  const [best, second] = ranked;
  const bestScore = best![1].score;
  const secondScore = second?.[1].score ?? 0;
  // Kitchen, dining and living (or living and office) coexist in integrated
  // rooms; bedroom with kitchen or living with bedroom are contradictory.
  const compatible = (a: RoomFunction, b: RoomFunction): boolean => {
    const pair = new Set([a, b]);
    if (pair.size === 1) return true;
    const social: RoomFunction[] = ["kitchen", "dining", "living", "leisure"];
    if (social.includes(a) && social.includes(b)) return true;
    if (pair.has("office") && (pair.has("living") || pair.has("bedroom"))) return true;
    return false;
  };
  if (second && secondScore >= Math.max(2, bestScore * 0.6) && !compatible(best![0], second[0])) {
    return {
      value: "undefined",
      confidence: "undefined",
      evidence: [
        `sinais incompatíveis: ${ROOM_FUNCTION_LABELS[best![0]]} (${best![1].evidence.slice(0, 3).join(", ")}) e ${ROOM_FUNCTION_LABELS[second[0]]} (${second[1].evidence.slice(0, 3).join(", ")})`,
      ],
    };
  }
  if (bestScore < 2) {
    return { value: "undefined", confidence: "undefined", evidence: [`sinal fraco de ${ROOM_FUNCTION_LABELS[best![0]]}: ${best![1].evidence.slice(0, 3).join(", ")}`] };
  }
  return { value: best![0], confidence: "inferred", evidence: best![1].evidence.slice(0, 6) };
}

/** Linhas de uma parede que lêem como "a mesma cota" que a informada. */
export function matchingLines(lines: ReadonlyArray<EnvironmentLine>, heightMm: number, toleranceMm = DATUM_TOLERANCE_MM): EnvironmentLine[] {
  return lines.filter((line) => Math.abs(line.heightMm - heightMm) <= toleranceMm);
}

/**
 * Um datum é uma linha da mesma natureza: base alinhada a base (ou peitoril),
 * topo alinhado a topo, teto ou verga. Topo do novo na base do existente (ou
 * vice-versa) é continuação, não alinhamento — lê como degrau e só vale como
 * decisão declarada.
 */
export function matchingLinesOfKind(
  lines: ReadonlyArray<EnvironmentLine>,
  heightMm: number,
  edge: "bottom" | "top",
  toleranceMm = DATUM_TOLERANCE_MM,
): EnvironmentLine[] {
  const kinds = edge === "bottom" ? new Set(["bottom", "opening-sill"]) : new Set(["top", "ceiling", "opening-head"]);
  return matchingLines(lines, heightMm, toleranceMm).filter((line) => kinds.has(line.kind));
}

export function describeLine(line: EnvironmentLine): string {
  const source = line.sourceName ? ` de ${line.sourceName}` : "";
  switch (line.kind) {
    case "ceiling": return `teto a ${Math.round(line.heightMm)} mm`;
    case "opening-head": return `verga${source} a ${Math.round(line.heightMm)} mm`;
    case "opening-sill": return `peitoril${source} a ${Math.round(line.heightMm)} mm`;
    case "top": return `topo${source} a ${Math.round(line.heightMm)} mm`;
    default: return `base${source} a ${Math.round(line.heightMm)} mm`;
  }
}

export function findReadingElement(reading: EnvironmentReading, subject: string): EnvironmentElement | null {
  const exact = reading.elements.filter((element) => normalizeText(element.name) === normalizeText(subject));
  if (exact.length === 1) return exact[0]!;
  const referring = reading.elements.filter((element) => planSubjectsRefer(element.name, subject) || planSubjectsRefer(subject, element.name));
  if (referring.length === 1) return referring[0]!;
  return null;
}

export interface PlanEnvironmentIssue {
  subject: string;
  /** veto: não pode ser executado sem correção; objection: precisa de justificativa/premissa registrada no plano. */
  severity: "veto" | "objection";
  message: string;
}

export interface PlanEnvironmentOptions {
  /** Altura do sujeito quando conhecida fora da decisão (dimensão-alvo de uma resolução). */
  subjectHeightMm?: (subject: string) => number | null;
  /** Identificador do elemento de referência de uma decisão quando o plano o conhece fora do campo referenceElementId (por exemplo, pelos critérios de revisão). */
  referenceElementId?: (decision: TaskSpatialDecision) => string | null;
  /** Nome de catálogo ou descrição do sujeito para tipologia e tipo de abertura. */
  subjectDescriptor?: (subject: string) => TypologyDescriptor | null;
  toleranceMm?: number;
}

/**
 * A premissa que declara a atividade real de uma superfície nomeada. É ela que
 * seleciona a faixa da régua — a declaração é auditável no plano e o revisor
 * de prancheta pode contestá-la; a régua deixa de adivinhar.
 */
export function declaredSurfaceActivity(
  assumptions: ReadonlyArray<{ topic?: string; subject?: string; surfaceActivity?: string; assumption?: string }>,
  names: ReadonlyArray<string | null | undefined>,
): { activity: SurfaceActivity; assumption: string } | null {
  const candidates = names
    .map((name) => normalizeText(name ?? ""))
    .filter((name) => name.length >= 3);
  for (const entry of assumptions) {
    if (!entry.surfaceActivity || entry.surfaceActivity === "unknown" || !entry.subject) continue;
    const subject = normalizeText(entry.subject);
    if (subject.length < 3) continue;
    if (!candidates.some((name) => name.includes(subject) || subject.includes(name))) continue;
    return { activity: entry.surfaceActivity as SurfaceActivity, assumption: entry.assumption ?? "" };
  }
  return null;
}

/**
 * Valida as decisões espaciais verticais de elementos suspensos contra a
 * leitura do ambiente. Cada objeção diz o que registrar (cota, datum, premissa)
 * — é uma gate com saída, não um veto cego.
 */
export function planEnvironmentIssues(
  proposal: Pick<TaskPlanProposal, "spatialDecisions" | "designResolutions" | "assumptions">,
  reading: EnvironmentReading,
  options: PlanEnvironmentOptions = {},
): PlanEnvironmentIssue[] {
  const issues: PlanEnvironmentIssue[] = [];
  const tolerance = options.toleranceMm ?? DATUM_TOLERANCE_MM;
  const assumptions = proposal.assumptions ?? [];
  const hasAssumption = (topic: string, ...terms: string[]) => assumptions.some((entry) => (
    entry.topic === topic || terms.some((term) => normalizeText(entry.assumption).includes(term))
  ));
  for (const decision of proposal.spatialDecisions) {
    const vertical = decision.relation === "above" || decision.relation === "below" || decision.relation === "stacked";
    if (!vertical) continue;
    if (decision.relation === "stacked") continue;
    const descriptor = options.subjectDescriptor?.(decision.subject) ?? { name: decision.subject };
    const subjectTypology = classifyInstallationTypology({ ...descriptor, name: descriptor.name ?? decision.subject });
    const suspended = decision.support === "wall" || decision.support === "ceiling" || isSuspendedTypology(subjectTypology.kind);
    if (!suspended) continue;
    const referenceId = decision.referenceElementId ?? options.referenceElementId?.(decision) ?? null;
    const reference = (referenceId ? reading.elements.find((candidate) => candidate.elementId === referenceId) ?? null : null)
      ?? findReadingElement(reading, decision.reference);
    if (!reference) {
      issues.push({
        subject: decision.subject,
        severity: "objection",
        message: `A referência “${decision.reference}” da decisão vertical de “${decision.subject}” não corresponde a um elemento da leitura do ambiente (${reading.elements.slice(0, 8).map((candidate) => candidate.name).join(", ") || "sem elementos"}); informe referenceElementId ou use o nome real do elemento para que a cota seja conferida contra as linhas da parede e a atividade da superfície.`,
      });
    }
    const subjectHeight = options.subjectHeightMm?.(decision.subject) ?? null;
    const referenceTop = reference ? reference.topMm : null;
    const explicitBottom = decision.bottomHeightMm ?? null;
    const explicitTop = decision.topHeightMm ?? null;
    let bottom: number | null = explicitBottom;
    let top: number | null = explicitTop;
    if (bottom === null && top !== null && subjectHeight !== null) bottom = top - subjectHeight;
    if (top === null && bottom !== null && subjectHeight !== null) top = bottom + subjectHeight;
    if (bottom === null && decision.relation === "above" && referenceTop !== null && decision.clearanceMm) {
      bottom = referenceTop + decision.clearanceMm.target;
      if (top === null && subjectHeight !== null) top = bottom + subjectHeight;
    }
    if (explicitBottom === null && explicitTop === null) {
      issues.push({
        subject: decision.subject,
        severity: "veto",
        message: `A decisão vertical de “${decision.subject}” precisa da cota absoluta: registre bottomHeightMm (face inferior em relação ao piso) ou topHeightMm, com datum (linha existente, teto, verga, faixa de uso ou linha nova justificada)${bottom !== null ? ` — pela folga registrada a face inferior ficaria a ${Math.round(bottom)} mm` : ""}.`,
      });
    }
    if (bottom === null) continue;

    // The plan may move the reference itself before installing above it
    // (a base placed beside a tall unit, then a module over the base): the
    // lines that matter are those of the wall the reference will stand against,
    // not of the wall it stands against today. Follow one level of that chain.
    const lateralRelations = new Set(["beside", "against", "aligned", "centered", "inside"]);
    const plannedMove = proposal.spatialDecisions.find((candidate) => (
      candidate !== decision
      && lateralRelations.has(candidate.relation)
      && (normalizeText(candidate.subject) === normalizeText(decision.reference)
        || (decision.referenceElementId !== undefined && candidate.subject === decision.reference))
    ));
    const movedAgainst = plannedMove
      ? (plannedMove.referenceElementId ? reading.elements.find((candidate) => candidate.elementId === plannedMove.referenceElementId) ?? null : null)
        ?? findReadingElement(reading, plannedMove.reference)
      : null;
    const wallId = movedAgainst?.wallId ?? reference?.wallId ?? null;
    const wall = wallId ? reading.walls.find((entry) => entry.wallId === wallId) ?? null : null;
    const lines = wall?.lines ?? [];
    const datum = decision.datum ?? null;
    const matchesBottom = matchingLinesOfKind(lines, bottom, "bottom", tolerance);
    const matchesTop = top !== null ? matchingLinesOfKind(lines, top, "top", tolerance) : [];
    const crossKind = matchesBottom.length === 0 && matchesTop.length === 0
      && (matchingLines(lines, bottom, tolerance).length > 0 || (top !== null && matchingLines(lines, top, tolerance).length > 0));
    // A declared, justified continuation (new top on an existing bottom, or
    // the reverse) is accepted as a datum; an undeclared one reads as a step.
    const alignedToExistingLine = matchesBottom.length > 0 || matchesTop.length > 0
      || (crossKind && datum?.kind === "existing-line" && (datum.justification ?? "").trim().length >= 10);

    // 1. Faixa da atividade na superfície de baixo (régua da pessoa + régua do par).
    const surface = reference ? reading.surfaces.find((entry) => entry.elementId === reference.elementId) ?? null : null;
    const lowerKind: InstallationTypology = reference ? reference.typology.kind : "unknown";
    const pairRule = verticalClearanceRule({ upper: subjectTypology.kind, lower: lowerKind, relation: "above", support: decision.support });
    // The declared activity of the reference surface — a structured premise —
    // beats the reading's guess: the plan states the reality it designs for
    // and the band follows from that statement.
    const declaredUse = declaredSurfaceActivity(assumptions, [decision.reference, reference?.name]);
    const band = verticalPlacementBand({
      activity: declaredUse?.activity ?? surface?.activity ?? "unknown",
      activityConfidence: declaredUse ? "declared" : ((surface?.confidence ?? "low") as ActivityConfidence),
      pairBottomHeightMm: pairRule?.bottomHeightMm ?? null,
      pairClearanceMm: pairRule?.typicalMm ?? null,
      pairMinimumMm: pairRule?.minimumMm ?? null,
      upperKind: subjectTypology.kind,
    });
    const storage = subjectTypology.kind === "upper_cabinet" || subjectTypology.kind === "shelf" || subjectTypology.kind === "unknown";
    if (storage && band.bottomHeightMm) {
      const [low, high] = band.bottomHeightMm;
      const outside = bottom < low - tolerance || bottom > high + tolerance;
      const overrideByLine = band.lineMayOverride && alignedToExistingLine && datum && (datum.kind === "existing-line" || datum.kind === "ceiling" || datum.kind === "opening-head");
      if (outside && !overrideByLine) {
        const activityLabel = band.activity !== "unknown" ? ACTIVITY_LABELS[band.activity] : "atividade indefinida";
        issues.push({
          subject: decision.subject,
          severity: band.lineMayOverride ? "objection" : "veto",
          message: `A face inferior de “${decision.subject}” a ${Math.round(bottom)} mm fica fora da faixa de uso para ${activityLabel} (${low}–${high} mm do piso${band.clearanceMm ? `; vão de ${band.clearanceMm[0]}–${band.clearanceMm[1]} mm sobre a superfície` : ""}).${band.lineMayOverride && lines.length ? ` Alternativa: alinhar a uma linha existente da parede (${lines.slice(0, 6).map(describeLine).join("; ")}) e registrar datum.kind=existing-line.` : ""} Corrija a cota ou registre a linha adotada.`,
        });
      }
    }

    // 1b. O vão sobre a referência: a cota absoluta, a folga declarada e a
    // faixa da atividade/par têm de contar a mesma história. Um contrato
    // duplo (cota diz 910, folga diz 900) ou uma folga admitida fora da faixa
    // vira uma rodada inteira de laudo + emenda + recolocação na execução.
    const referenceRecotated = proposal.spatialDecisions.some((candidate) => (
      candidate !== decision
      && (candidate.relation === "above" || candidate.relation === "below" || candidate.relation === "stacked")
      && normalizeText(candidate.subject) === normalizeText(decision.reference)
    ));
    if (decision.relation === "above" && referenceTop !== null && !referenceRecotated) {
      const impliedClearance = bottom - referenceTop;
      const declared = decision.clearanceMm ?? null;
      const referenceLabel = `${reference?.name ?? decision.reference} (topo a ${Math.round(referenceTop)} mm)`;
      // The execution honours bottomHeightMm and then checks the built gap
      // against clearanceMm — strictly. A cota outside its own declared range
      // is therefore a plan that fails its own verification (1650 over 740
      // with maximum 900 = a 10 mm nudge round, 19/08 19:41).
      if (declared && (impliedClearance < declared.minimum - 1 || impliedClearance > declared.maximum + 1)) {
        issues.push({
          subject: decision.subject,
          severity: "objection",
          message: `“${decision.subject}” carrega dois contratos verticais: bottomHeightMm ${Math.round(bottom)} mm implica vão de ${Math.round(impliedClearance)} mm sobre ${referenceLabel}, fora da folga declarada de ${declared.minimum}–${declared.maximum} mm. Registre um só: ajuste a cota ou a folga para que coincidam.`,
        });
      }
      if (pairRule && impliedClearance < pairRule.minimumMm - tolerance && !decision.allowContact) {
        issues.push({
          subject: decision.subject,
          severity: "veto",
          message: `A face inferior de “${decision.subject}” a ${Math.round(bottom)} mm deixa ${Math.round(impliedClearance)} mm sobre ${referenceLabel}: abaixo do mínimo de ${pairRule.minimumMm} mm do par (${pairRule.summary}). Suba a cota ou declare empilhamento com apoio (relation stacked / allowContact).`,
        });
      } else if (band.clearanceMm) {
        const [lowGap, highGap] = band.clearanceMm;
        const overrideByLine = band.lineMayOverride && alignedToExistingLine && datum && (datum.kind === "existing-line" || datum.kind === "ceiling" || datum.kind === "opening-head");
        const outsideGap = impliedClearance < lowGap - tolerance || impliedClearance > highGap + tolerance;
        // The declared minimum is a floor the execution verifies; the declared
        // maximum is what lets the target drift above the band (850–910–950
        // over a 740 support, 19/08 19:34), so only the ceiling is judged here.
        const declaredOutside = declared !== null && declared.maximum > highGap + tolerance;
        const gapPremise = hasAssumption("activity", "vao", "folga");
        if ((outsideGap || declaredOutside) && !overrideByLine && !gapPremise) {
          const activityLabel = band.activity !== "unknown" ? ACTIVITY_LABELS[band.activity] : "atividade indefinida";
          const what = outsideGap
            ? `a cota deixa vão de ${Math.round(impliedClearance)} mm sobre ${referenceLabel}`
            : `a folga declarada admite ${declared!.minimum}–${declared!.maximum} mm sobre ${referenceLabel}`;
          issues.push({
            subject: decision.subject,
            severity: "objection",
            message: `“${decision.subject}”: ${what}, fora da faixa de ${lowGap}–${highGap} mm para ${activityLabel}${band.basis.length ? ` (${band.basis.join("; ")})` : ""}. Ajuste a cota e a folga para dentro da faixa${band.lineMayOverride && lines.length ? `, ou alinhe a uma linha existente da parede (${lines.slice(0, 6).map(describeLine).join("; ")}) com datum.kind=existing-line` : ""}, ou registre em assumptions a premissa que justifica o vão.`,
          });
        }
      }
    }

    // 2. Linhas existentes da mesma família na parede: alinhar ou justificar.
    const familyLines = lines.filter((line) => line.family === "suspended");
    const claimsExistingLine = datum?.kind === "existing-line" || datum?.kind === "ceiling" || datum?.kind === "opening-head";
    if (claimsExistingLine && !alignedToExistingLine) {
      issues.push({
        subject: decision.subject,
        severity: "objection",
        message: crossKind
          ? `“${decision.subject}” alinha ${Math.round(bottom)} mm${top !== null ? `/${Math.round(top)} mm` : ""} a uma linha de natureza oposta (topo do novo na base do existente, ou o inverso): isso lê como degrau, não como alinhamento. Alinhe base com base ou topo com topo (${lines.slice(0, 6).map(describeLine).join("; ")}), ou registre a continuação como decisão com justification no datum.`
          : lines.length
          ? `“${decision.subject}” declara alinhamento a uma linha existente, mas a cota ${Math.round(bottom)} mm${top !== null ? `/${Math.round(top)} mm` : ""} não coincide com nenhuma linha da parede (${lines.slice(0, 6).map(describeLine).join("; ")}; tolerância ${tolerance} mm). Ajuste a cota ou o datum.`
          : `“${decision.subject}” declara datum ${datum?.kind}, mas a leitura do ambiente não registra linhas na parede de “${decision.reference}”; use use-band ou new-line com justificativa.`,
      });
    } else if (familyLines.length && !alignedToExistingLine) {
      const justified = datum?.kind === "new-line" && (datum.justification ?? "").trim().length >= 10;
      if (!justified) {
        issues.push({
          subject: decision.subject,
          severity: "objection",
          message: `A parede de “${decision.reference}” já tem linha de elementos suspensos (${familyLines.slice(0, 6).map(describeLine).join("; ")}); “${decision.subject}” abriria uma linha nova (face inferior ${Math.round(bottom)} mm${top !== null ? `, topo ${Math.round(top)} mm` : ""}). Alinhe topo ou base a uma linha existente (datum.kind=existing-line, reference=elemento) ou registre datum.kind=new-line com a justificativa de composição.`,
        });
      }
    }

    // 3. Alcance do conteúdo e teto de guarda.
    if (storage && top !== null) {
      const reach = reachAssessment({ bottomMm: bottom, topMm: top });
      const highStorageDeclared = hasAssumption("reach", "esporad", "guarda alta", "maleiro", "escada")
        || (datum && (datum.kind === "existing-line" || datum.kind === "ceiling") && alignedToExistingLine);
      if ((reach.status === "out_of_reach" || reach.status === "occasional") && !highStorageDeclared) {
        issues.push({ subject: decision.subject, severity: "objection", message: `${reach.message} Registre em assumptions (topic reach) que a guarda é esporádica ou alinhe a uma linha existente com datum.` });
      }
      if (top > STANDING_ADULT.reach.storageTopMaxMm + tolerance && !highStorageDeclared && datum?.kind !== "ceiling") {
        issues.push({ subject: decision.subject, severity: "objection", message: `O topo de “${decision.subject}” a ${Math.round(top)} mm passa do teto de guarda de uso regular (${STANDING_ADULT.reach.storageTopMaxMm} mm). Só como decisão declarada (torre até o teto, linha existente, guarda esporádica).` });
      }
    }

    // 4. Envelope de operação (folha aberta) na zona de cabeça.
    if (top !== null) {
      const opening = inferOpeningKind({ name: descriptor.name ?? decision.subject, description: descriptor.description ?? null });
      const projection = subjectHeight ?? (top - bottom);
      const standoff = reference ? reference.depthMm : 0;
      const envelope = operationEnvelopeAssessment({ opening, bottomMm: bottom, topMm: top, projectionMm: projection, standoffMm: standoff });
      if (envelope.status === "head_zone" && envelope.message) {
        issues.push({ subject: decision.subject, severity: "objection", message: envelope.message });
      }
    }

    // 5. Premissa de função do cômodo quando a faixa depende dela.
    if (storage && reading.roomFunction.value === "undefined" && (surface?.confidence === "low" || surface?.confidence === "medium" || !surface)
      && !hasAssumption("room-function", "funcao", "função", "atividade", "uso")) {
      issues.push({
        subject: decision.subject,
        severity: "objection",
        message: `A função do ambiente é indefinida (${reading.roomFunction.evidence.join("; ") || "sem evidência"}) e a cota de “${decision.subject}” depende da atividade na superfície de baixo. Registre em assumptions (topic room-function ou activity) a premissa adotada — ela aparece na resposta final.`,
      });
    }
  }
  return dedupeIssues(issues);
}

function dedupeIssues(issues: PlanEnvironmentIssue[]): PlanEnvironmentIssue[] {
  const seen = new Set<string>();
  return issues.filter((issue) => {
    const key = `${issue.subject}|${issue.message}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * Candidatos de altura para um suspenso novo sobre uma referência: a faixa da
 * atividade, cada linha existente da parede (topo ou base alinhados) e o teto.
 * Devolvidos pelas ferramentas de implantação para o planejador comparar
 * alternativas reais em vez de inventar um número.
 */
export interface HeightCandidate {
  label: string;
  datumKind: "use-band" | "existing-line" | "ceiling" | "opening-head";
  datumReference: string | null;
  bottomHeightMm: number;
  topHeightMm: number | null;
  clearanceMm: number | null;
  reach: string | null;
  notes: string[];
}

export function heightCandidates(input: {
  reading: EnvironmentReading;
  reference: EnvironmentElement | null;
  subjectKind: InstallationTypology;
  subjectHeightMm: number | null;
  activity: SurfaceActivity;
  activityConfidence: ActivityConfidence;
  pairBottomHeightMm: [number, number] | null;
  pairClearanceMm: [number, number] | null;
  pairMinimumMm: number | null;
}): HeightCandidate[] {
  const candidates: HeightCandidate[] = [];
  const band = verticalPlacementBand({
    activity: input.activity,
    activityConfidence: input.activityConfidence,
    pairBottomHeightMm: input.pairBottomHeightMm,
    pairClearanceMm: input.pairClearanceMm,
    pairMinimumMm: input.pairMinimumMm,
    upperKind: input.subjectKind,
  });
  const referenceTop = input.reference?.topMm ?? null;
  const height = input.subjectHeightMm;
  const storage = input.subjectKind === "upper_cabinet" || input.subjectKind === "shelf" || input.subjectKind === "unknown";
  const describeReach = (bottom: number): string | null => (storage && height !== null ? reachAssessment({ bottomMm: bottom, topMm: bottom + height }).message : null);
  if (band.bottomHeightMm) {
    const [low, high] = band.bottomHeightMm;
    let bottom = Math.round((low + high) / 2 / 10) * 10;
    if (referenceTop !== null && band.clearanceMm) {
      const byClearance = referenceTop + Math.round((band.clearanceMm[0] + band.clearanceMm[1]) / 2);
      bottom = Math.min(high, Math.max(low, byClearance));
    }
    candidates.push({
      label: `faixa de uso (${ACTIVITY_LABELS[band.activity]})`,
      datumKind: "use-band",
      datumReference: null,
      bottomHeightMm: bottom,
      topHeightMm: height !== null ? bottom + height : null,
      clearanceMm: referenceTop !== null ? bottom - referenceTop : null,
      reach: describeReach(bottom),
      notes: [`face inferior entre ${low} e ${high} mm`, ...band.basis],
    });
  }
  const wall = input.reference?.wallId ? input.reading.walls.find((entry) => entry.wallId === input.reference!.wallId) ?? null : null;
  const seen = new Set<number>();
  // O teto e as linhas da mesma família são os datums mais fortes; quando duas
  // linhas produzem a mesma cota, a mais forte fica com o rótulo.
  const priority = (line: EnvironmentLine): number => line.kind === "ceiling" ? 0 : line.family === "suspended" ? 1 : line.family === "opening" ? 2 : 3;
  const orderedLines = [...(wall?.lines ?? [])].sort((left, right) => priority(left) - priority(right));
  for (const line of orderedLines) {
    if (line.sourceElementId && input.reference && line.sourceElementId === input.reference.elementId) continue;
    const options: Array<{ bottom: number; how: string }> = [];
    if (line.kind === "bottom" || line.kind === "opening-sill") {
      options.push({ bottom: line.heightMm, how: "base alinhada" });
      if (height !== null) options.push({ bottom: line.heightMm - height, how: "topo na linha da base (continuação — exige justification no datum)" });
    }
    if (line.kind === "top" || line.kind === "opening-head") {
      options.push({ bottom: line.heightMm, how: "base na linha do topo (continuação — exige justification no datum)" });
      if (height !== null) options.push({ bottom: line.heightMm - height, how: "topo alinhado" });
    }
    if (line.kind === "ceiling" && height !== null) options.push({ bottom: line.heightMm - height, how: "topo no teto" });
    for (const option of options) {
      const bottom = Math.round(option.bottom);
      if (referenceTop !== null && bottom < referenceTop + (input.pairMinimumMm ?? 0)) continue;
      if (seen.has(bottom)) continue;
      seen.add(bottom);
      candidates.push({
        label: `${option.how}: ${describeLine(line)}`,
        datumKind: line.kind === "ceiling" ? "ceiling" : line.kind === "opening-head" ? "opening-head" : "existing-line",
        datumReference: line.sourceName ?? (line.kind === "ceiling" ? "teto" : null),
        bottomHeightMm: bottom,
        topHeightMm: height !== null ? bottom + height : null,
        clearanceMm: referenceTop !== null ? bottom - referenceTop : null,
        reach: describeReach(bottom),
        notes: [line.family === "suspended" ? "mesma família de suspensos na parede" : `linha de ${line.family === "floor-standing" ? "elemento de piso" : line.family}`],
      });
    }
  }
  return candidates.slice(0, 8);
}

export function summarizeEnvironmentReading(reading: EnvironmentReading): string {
  const parts: string[] = [];
  parts.push(`Ambiente ${reading.roomName ?? reading.roomId ?? "único"}: função ${ROOM_FUNCTION_LABELS[reading.roomFunction.value]} (${reading.roomFunction.confidence === "declared" ? "declarada" : reading.roomFunction.confidence === "inferred" ? "inferida" : "indefinida"}); pé-direito ${Math.round(reading.ceilingHeightMm)} mm; perfil ${reading.userProfile.label}.`);
  for (const wall of reading.walls) {
    if (!wall.elementIds.length && !wall.lines.some((line) => line.family === "opening")) continue;
    const lines = wall.lines.filter((line) => line.kind !== "ceiling").map(describeLine).slice(0, 8);
    parts.push(`${wall.wallId}: ${wall.elementIds.length} elemento(s); linhas: ${lines.join("; ") || "nenhuma"}.`);
  }
  for (const surface of reading.surfaces.slice(0, 8)) {
    parts.push(`${surface.name}: superfície a ${Math.round(surface.topMm)} mm, atividade ${ACTIVITY_LABELS[surface.activity]} (${surface.confidence}).`);
  }
  return parts.join(" ");
}

export type { RoomFunction, SurfaceActivity, TaskSpatialDecision };
